import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { DialogTitle } from '@savvy-kit/components/dialog';
import ItemDialogBackButton from 'src/components/mui/item-dialog-back-button';
import ItemHeader from 'src/components/portfolio/views/item-header';
import TrackedDialog from 'src/components/tracked/tracked-dialog';
import { graphql, unmaskFragment } from 'src/gql';

import type { PortfolioItemTypeName } from '@savvy/constants/generated/gql-enums';
import defaultGuard from '@savvy/utils/default-guard';

import FinancialAccountForm from './financial-account-form';

export const EditFinancialAccountDialogFragment = graphql(/* GraphQL */ `
  fragment EditFinancialAccountDialog on Account {
    ...FinancialAccountForm
    type
  }
`);

function getTitle(accountType: PortfolioItemTypeName) {
  const defaultText = 'Account details';
  switch (accountType) {
    case 'investment':
      return 'Investment account details';
    case 'depository':
      return 'Bank account details';
    case 'auto':
    case 'commercial':
    case 'credit':
    case 'insurance':
    case 'loan':
    case 'mortgage':
    case 'student':
    case 'consumer':
    case 'cryptocurrency':
    case 'employer_equity':
    case 'fund_investment':
    case 'general':
    case 'home_equity':
    case 'line_of_credit':
    case 'other':
    case 'private_investment':
      return defaultText;
    default:
      return defaultGuard(accountType, defaultText);
  }
}

type EditFinancialAccountDialogProps = {
  readonly accountFrag: FragmentType<typeof EditFinancialAccountDialogFragment>;
  readonly onClose: () => void;
  readonly onBack: () => void;
  readonly onEdit: Deprecated_HandleItemUpdate;
};

export default function EditFinancialAccountDialog({
  accountFrag,
  onClose,
  onBack,
  onEdit,
}: EditFinancialAccountDialogProps) {
  const account = unmaskFragment(EditFinancialAccountDialogFragment, accountFrag);
  return (
    <TrackedDialog
      elementName="edit-financial-account-dialog"
      open
      onOpenChange={(nextOpen) => !nextOpen && onClose()}
      className="flex max-h-[85vh] flex-col p-0"
    >
      <DialogTitle className="sr-only">Edit financial account</DialogTitle>
      <ItemDialogBackButton trackedElementName="edit-financial-account-dialog-back-button" onClick={onBack} />
      {/* pt-20 clears the absolute ItemDialogBackButton above (top-6 + h-10). */}
      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-20">
        <ItemHeader text={getTitle(account.type)} />
        <FinancialAccountForm onSuccess={onEdit} accountFrag={account} />
      </div>
    </TrackedDialog>
  );
}
